import React, { useCallback, useLayoutEffect } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { useCart } from '../../hooks';
import { Customization } from './components';

// ────────────────────────────────────────────────────────────────────────────────

export const CustomizationEditScreen = () => {
  const router = useRouter();
  const routeParams = useLocalSearchParams<CustomizationEditScreenRouteParams>();

  const { restaurantSlug, productSlug, lineItemId } = routeParams;

  const { cart } = useCart();

  // ─────────────────────────────────────────────────────────────────

  const navigateToBag = useCallback(() => {
    try {
      router.dismissTo('/bag');
    } catch {
      // expo-router throws if called before Root Layout mounts
    }
  }, [router]);

  const navigateToCustomizationScreen = useCallback(() => {
    try {
      router.replace({
        pathname: '/[restaurantSlug]/[productSlug]',
        params: {
          restaurantSlug: restaurantSlug ?? '',
          productSlug: productSlug ?? '',
        },
      });
    } catch {
      // expo-router throws if called before Root Layout mounts
    }
  }, [router, productSlug, restaurantSlug]);

  // ─────────────────────────────────────────────────────────────────

  // redirect to the customization screen if the line item isn't in the cart or has been removed
  useLayoutEffect(() => {
    const lineItems = cart?.lineItems;
    const isLineItemInCart = lineItems?.some(({ id }) => id === lineItemId);

    if (isLineItemInCart) return;

    navigateToCustomizationScreen();
  }, [cart?.lineItems, lineItemId, navigateToCustomizationScreen]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Customization
      restaurantSlug={restaurantSlug}
      productSlug={productSlug}
      lineItemId={lineItemId}
      isEditProductMode={true}
      onEditLineItemInCart={navigateToBag}
      onRemoveLineItemFromCart={navigateToBag}
    />
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationEditScreenRouteParams = {
  restaurantSlug: string;
  productSlug: string;
  lineItemId: string;
};
